import type { ReactNode } from "react";
import { cn } from "./cn";

export type UnkDurum = "tamamlandi" | "eksik" | "bekliyor" | "planli";

/**
 * Durum dörtlüsünün sınıfları — rozetten ayrı da kullanılıyor: gün kartının sol
 * renkli çizgisi (`cizgi`), gösterge noktası (`nokta`), açık zeminli kutu.
 * Sınıf adları TAM yazılı: Tailwind birleştirilmiş adı tarayamaz.
 */
export const DURUM_SINIFLARI: Record<
  UnkDurum,
  { nokta: string; cizgi: string; zemin: string; metin: string; halka: string }
> = {
  tamamlandi: {
    nokta: "bg-tamam-nokta",
    cizgi: "border-l-tamam-nokta",
    zemin: "bg-tamam-zemin",
    metin: "text-tamam-metin",
    halka: "ring-tamam-nokta/30",
  },
  eksik: {
    nokta: "bg-eksik-nokta",
    cizgi: "border-l-eksik-nokta",
    zemin: "bg-eksik-zemin",
    metin: "text-eksik-metin",
    halka: "ring-eksik-nokta/30",
  },
  bekliyor: {
    nokta: "bg-bekliyor-nokta",
    cizgi: "border-l-bekliyor-nokta",
    zemin: "bg-bekliyor-zemin",
    metin: "text-bekliyor-metin",
    halka: "ring-bekliyor-nokta/40",
  },
  planli: {
    nokta: "bg-planli-nokta",
    cizgi: "border-l-planli-nokta",
    zemin: "bg-planli-zemin",
    metin: "text-planli-metin",
    halka: "ring-planli-nokta/30",
  },
};

export const DURUM_ETIKETI: Record<UnkDurum, string> = {
  tamamlandi: "Tamamlandı",
  eksik: "Eksik",
  bekliyor: "Bekliyor",
  planli: "Planlı",
};

export interface DurumRozetiProps {
  durum: UnkDurum;
  /** Ekrana özgü metin ("Rapor girilmedi"); verilmezse durumun varsayılan adı. */
  children?: ReactNode;
  className?: string;
}

/**
 * DURUM ROZETİ — UNİKOÇ dört durum rengi (sürüm 9a; tarama-2 §2.13)
 *
 *   yeşil   tamamlandı   kırmızı  eksik / rapor girilmedi
 *   turuncu bekliyor      mavi     planlı
 *
 * NOKTA ölçülen rengin kendisi; METİN aynı ailenin koyusu. Ölçülen renkler
 * beyaz yazıyla AA'yı geçmiyor (yeşil 3,22 · turuncu 1,96) — eski panel de dolu
 * rozet değil açık hap kullanıyordu (ViewAsStudent "Tamamlandı" hapı). Durum
 * yalnız renkle değil, metinle de söyleniyor: renk körü kullanıcı metni okur.
 */
export function DurumRozeti({ durum, children, className }: DurumRozetiProps) {
  const s = DURUM_SINIFLARI[durum];
  return (
    <span
      data-durum={durum}
      className={cn(
        "rounded-rozet inline-flex items-center gap-1.5 px-2.5 py-0.5",
        "text-xs font-semibold ring-1 ring-inset",
        s.zemin,
        s.metin,
        s.halka,
        className,
      )}
    >
      <span aria-hidden="true" className={cn("size-2 shrink-0 rounded-full", s.nokta)} />
      {children ?? DURUM_ETIKETI[durum]}
    </span>
  );
}
